// iter_302 · 設定 → 書類とナレッジ → 書類読み取り学習
// 主要な区分は4つだけ（概要・学習する・承認する・効果を確認）。
// 技術的な内容は各区分の中で段階的に開く。
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, X, BookOpen } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { LEARN, authHeader, detailOf, Panel } from './learning/shared';
import Overview from './learning/Overview';
import Teach from './learning/Teach';
import Approve from './learning/Approve';
import Evaluate from './learning/Evaluate';

const AREAS = [['overview', '概要'], ['teach', '学習する'],
  ['approve', '承認する'], ['evaluate', '効果を確認']];
const SEEN_KEY = 'doc_learning_guide_seen';

function FirstRun({ principles, onStart, onGuide, onClose }) {
  return (
    <Panel testId="learn-firstrun">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 max-w-2xl">
          <h2 className="text-base font-semibold text-zinc-900">書類読み取り学習へようこそ</h2>
          <p className="pt-1 text-[12.5px] leading-relaxed text-zinc-700">
            実際の書類と確認結果を使って、今後の読み取りを改善できます。
            書類を追加しただけでは、システム全体の読み取り方法は変わりません。
            管理者が学習候補を確認し、検証後に有効にした内容だけが使用されます。
          </p>
          <ol className="pt-2 space-y-1">
            {(principles || []).map((p, i) => (
              <li key={p} className="text-[12px] text-zinc-700"
                data-testid={`learn-principle-${i + 1}`}>{i + 1}. {p}</li>
            ))}
          </ol>
          <div className="flex flex-wrap items-center gap-2 pt-3">
            <Button className="h-10 bg-teal-700 text-[12.5px] hover:bg-teal-800" onClick={onStart}
              data-testid="learn-firstrun-start">
              <Plus size={13} className="mr-1" />学習用の書類を追加
            </Button>
            <Button variant="outline" className="h-10 text-[12px]" onClick={onGuide}
              data-testid="learn-firstrun-guide">
              <BookOpen size={13} className="mr-1" />使い方を見る
            </Button>
          </div>
        </div>
        <button type="button" onClick={onClose} aria-label="案内を閉じる"
          className="min-h-[32px] min-w-[32px] text-zinc-400 hover:text-zinc-700"
          data-testid="learn-firstrun-close">
          <X size={16} />
        </button>
      </div>
    </Panel>
  );
}

export default function DocumentLearning() {
  const [area, setArea] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showFirstRun, setShowFirstRun] = useState(
    () => localStorage.getItem(SEEN_KEY) !== '1');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${LEARN}/overview`, { headers: authHeader() });
      setOverview(r.data);
    } catch (e) {
      toast.error(detailOf(e, '概要を取得できませんでした'));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const dismiss = () => {
    localStorage.setItem(SEEN_KEY, '1');
    setShowFirstRun(false);
  };
  const openGuide = () => { window.location.href = '/settings/document-learning/guide'; };

  return (
    <div className="space-y-4 p-4 sm:p-6" data-testid="doc-learning-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="max-w-2xl">
          <h1 className="text-2xl font-semibold text-zinc-900">書類読み取り学習</h1>
          <p className="pt-1 text-[12.5px] leading-relaxed text-zinc-600"
            data-testid="learn-purpose">
            {overview?.purpose_ja
              || '書類の読み取り結果を確認し、今後の読み取りに使うルールを管理します。'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" className="h-10 text-[12px]" onClick={openGuide}
            data-testid="learn-open-guide">
            <BookOpen size={13} className="mr-1" />使い方
          </Button>
          <Button className="h-10 bg-teal-700 text-[12.5px] hover:bg-teal-800"
            onClick={() => setArea('teach')} data-testid="learn-primary-add">
            <Plus size={13} className="mr-1" />学習用の書類を追加
          </Button>
        </div>
      </div>

      <nav className="flex flex-wrap gap-1.5" aria-label="区分" data-testid="learn-areas">
        {AREAS.map(([key, label]) => (
          <button key={key} type="button" onClick={() => setArea(key)}
            aria-current={area === key ? 'page' : undefined}
            className={`min-h-[36px] rounded-full border px-4 text-[12.5px] transition-colors duration-200 ${
              area === key ? 'border-teal-700 bg-teal-700 text-white'
                : 'border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50'}`}
            data-testid={`learn-area-${key}`}>{label}</button>
        ))}
      </nav>

      {showFirstRun && (
        <FirstRun principles={overview?.principles_ja}
          onStart={() => { dismiss(); setArea('teach'); }}
          onGuide={() => { dismiss(); openGuide(); }} onClose={dismiss} />
      )}

      {area === 'overview' && (
        <Overview data={overview} loading={loading} onReload={load} onGo={setArea} />
      )}
      {area === 'teach' && <Teach onGo={setArea} onChanged={load} />}
      {area === 'approve' && <Approve onChanged={load} />}
      {area === 'evaluate' && <Evaluate onChanged={load} onGo={setArea} />}

      <p className="text-[11px] leading-relaxed text-zinc-400" data-testid="learn-footer-note">
        この画面はモデルの重みを変更しません。学習内容はバージョン付きの設定として保存され、
        いつでも以前の内容に戻せます。参照元の書類の権限はそのまま適用されます。
      </p>
    </div>
  );
}
